/**
 * useTutorApplicationRecordingState — l'enregistrement d'une réponse vidéo, port de l'ancien
 * `VideoRecordingScreen` autour de son moteur (`useApplicationRecorder`) :
 *
 *  - la question lue dans la tranche des questions (`questionId`, en chaîne comme la route) ;
 *  - le temps de réponse (`answer_duration`) décompte à la seconde une fois l'enregistrement parti ;
 *  - « Terminer » confirme (« Veux-tu vraiment terminer l'enregistrement ? ») et met le temps à zéro,
 *    ce qui arrête l'enregistrement ;
 *  - la croix confirme (« Veux-tu vraiment quitter ? » / « Ta réponse ne sera pas enregistrée ») et
 *    revient à la question ;
 *  - la vidéo posée, on revient à la question, qui l'envoie.
 */
import { useNavigation } from "@react-navigation/native"
import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"

import { type TutorStudentWorkPart } from "../../../../../../design-system/organisms/TutorStudentWorkViewer/TutorStudentWorkViewer"
import { type TutorApplicationRecordingPageProps } from "../../../../../../design-system/pages/TutorApplicationRecordingPage/TutorApplicationRecordingPage"
import { useInterval } from "../../../../../../engine/shared/useInterval"
import {
    type ApiApplicationQuestion,
    recordingQuestionTitle,
    toUIVideoQuestion,
} from "../../../../adapters/tutor/Application/tutorVideoQuestionAdapter"
import { useApplicationRecorder } from "../../../hooks/useApplicationRecorder"

type RecordingState = { data?: { tutors?: { testQuestions?: ApiApplicationQuestion[] | null } } }

export const RECORDING_EXIT_MESSAGE = "Ta réponse ne sera pas enregistrée"

export function useTutorApplicationRecordingState(questionId: unknown): TutorApplicationRecordingPageProps {
    const navigation = useNavigation() as unknown as { goBack: () => void }
    const question = useSelector((state: RecordingState) =>
        (state?.data?.tutors?.testQuestions ?? []).find((q) => q?.id?.toString() === String(questionId))
    )
    // Mémorisée : le décompte re-rend la page à la seconde, et la copie (`React.memo`) n'a pas à le suivre.
    const ui = useMemo(() => toUIVideoQuestion(question), [question])

    const [secondsLeft, setSecondsLeft] = useState(Number(question?.answer_duration ?? 0))
    // Une question technique pose le viseur dans une case, à côté de la copie (`<TutorVideoRecorder work>`).
    const recorder = useApplicationRecorder({ secondsLeft, framed: ui?.work != null })
    const recording = recorder.phase === "recording"

    const ticking = recording && secondsLeft > 0
    useInterval(() => setSecondsLeft((left) => Math.max(left - 1, 0)), ticking ? 1000 : null)

    useEffect(() => {
        if (recorder.recorded) navigation.goBack()
        // Une fois, à la vidéo posée.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recorder.recorded])

    const [exitOpen, setExitOpen] = useState(false)
    const [finishOpen, setFinishOpen] = useState(false)
    const [workPart, setWorkPart] = useState<TutorStudentWorkPart>("copy")

    return {
        phase: recorder.phase,
        viewfinder: recorder.viewfinder,
        question: recordingQuestionTitle(question),
        work: ui?.work ?? null,
        secondsLeft,
        onFinish: () => setFinishOpen(true),
        onClose: () => setExitOpen(true),
        onAllow: recorder.allow,
        workPart,
        onChangeWorkPart: setWorkPart,
        exitConfirm: {
            visible: exitOpen,
            message: RECORDING_EXIT_MESSAGE,
            onConfirm: () => {
                setExitOpen(false)
                navigation.goBack()
            },
            onCancel: () => setExitOpen(false),
        },
        finishConfirm: {
            visible: finishOpen,
            onConfirm: () => {
                setFinishOpen(false)
                setSecondsLeft(0)
            },
            onCancel: () => setFinishOpen(false),
        },
    }
}
